// messagesSlice.ts - MINIMAL ADDITIONS
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getConversationsFromId, sendMessageToLead, downloadChatMedia } from '../../services/whatsapp';
import { RootState } from '../store';
import moment from 'moment';
import logger from '../../utils/logger';

// ✅ EXISTING INTERFACES - NO CHANGES
export interface Message {
  _id: string;
  messageId?: string;
  phone?: string;
  sender: any;
  statusLogs?: Array<{
    status: string;
    timestamp: string;
  }>;
  messageType?: 'text' | 'image' | 'video' | 'document' | 'audio' | 'reaction' | 'template' | 'button' | 'contacts';
  message: {
    emoji?: string;
    reaction?: {
      message_id: string;
      emoji: string;
    };
    body?: string | {
      type?: string;
      text?: {
        body: string;
      };
    };
    header?: Array<{
      type: string;
      format?: string;
      text?: string;
    }> | {
      type: string;
      parameters?: Array<{
        type: string;
        document?: {
          link: string;
          filename: string;
        }
      }>
    };
    footer?: Array<{
      type: string;
      text: string;
    }>;
    buttons?: Array<{
      type: string;
      buttons: Array<{
        type: string;
        text: string;
        phone_number?: string;
        url?: string;
      }>
    }>;
    id?: string;
    mime_type?: string;
    filename?: string;
    button?: {
      payload: string;
      text: string;
    };
    contacts?: Array<{
      name?: {
        formatted_name?: string;
        first_name?: string;
        middle_name?: string;
        last_name?: string;
      };
      phones?: Array<{
        phone: string;
        type?: string;
      }>;
    }>;
  };
  context?: {
    from: string;
    id: string;
    message: {
      header: any[];
      body: {
        type: string;
        text: {
          body: string;
        };
      };
      footer: Array<{
        type: string;
        text: string;
      }>;
      buttons: Array<{
        type: string;
        buttons: Array<{
          type: string;
          text: string;
        }>;
      }>;
    };
  };
  createdAt: string;
  messageDelivery?: 'sent' | 'delivered' | 'read' | 'failed' | 'sending';
  status?: 'sent' | 'delivered' | 'read' | 'failed' | 'received' | 'sending';
  downloadUrl?: string;
  reactions?: {
    [userId: string]: string;
  };
}

export interface ReactionItem {
  emoji: string;
  sender: string;
  timestamp: string;
}

export interface Lead {
  _id: string;
  name: string;
  phone: string;
  email?: string;
  status: string[];
  label: string[];
  [key: string]: any;
}

// ✅ MINIMAL ADDITION: Just 3 new fields for pagination
export interface MessagesState {
  messageReactions: { [messageId: string]: Array<{ emoji: string, sender: string, timestamp: string }> };
  messages: Message[];
  messagesByDate: {
    [date: string]: {
      [time: string]: Message[];
    };
  };
  currentConversationId: string | null;
  replyContext: Message | null;
  lead: Lead | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  searchText: string;
  mediaDownloading: {
    [messageId: string]: boolean;
  };
  mediaErrors: {
    [messageId: string]: string;
  };
  
  // ✅ NEW: Simple pagination fields
  hasMoreMessages: boolean;
  loadingOlderMessages: boolean;
  currentPage: number;
}

// ✅ UPDATE: Initial state with new fields
const initialState: MessagesState = {
  messages: [],
  messagesByDate: {},
  currentConversationId: null,
  replyContext: null,
  lead: null,
  status: 'idle',
  messageReactions: {},
  error: null,
  searchText: '',
  mediaDownloading: {},
  mediaErrors: {},
  
  
  // ✅ NEW: Default pagination values
  hasMoreMessages: true,
  loadingOlderMessages: false,
  currentPage: 1,
};

// ✅ EXISTING HELPER FUNCTIONS - NO CHANGES
const groupMessagesByDateTime = (messages: Message[]) => {
  const groupedByDate: {
    [date: string]: {
      [time: string]: Message[];
    };
  } = {};
  
  messages.forEach(message => {
    const date = moment(message.createdAt).format('L');
    const time = moment(message.createdAt).format('hh:mm a');
    
    if (!groupedByDate[date]) {
      groupedByDate[date] = {};
    }
    
    if (!groupedByDate[date][time]) {
      groupedByDate[date][time] = [];
    }
    
    groupedByDate[date][time].push(message);
  });
  
  return groupedByDate;
};

const getWhatsAppPhoneId = (userState: any, defaultPhoneId: string = ''): string => {
  let userPhoneId = defaultPhoneId;
  
  if (userState && Array.isArray(userState)) {
    const userDetails = userState[0];
    if (userDetails && userDetails.userIntegrations) {
      const whatsappIntegration = userDetails.userIntegrations.find(
        (e: any) => e?.integration?.key === "whatsapp_business"
      );
      if (whatsappIntegration?.details?.[0]?.phoneNumberId) {
        userPhoneId = whatsappIntegration.details[0].phoneNumberId;
      }
    }
  } else if (userState && typeof userState === 'object' && userState.userIntegrations) {
    const whatsappIntegration = userState.userIntegrations.find(
      (e: any) => e?.integration?.key === "whatsapp_business"
    );
    if (whatsappIntegration?.details?.[0]?.phoneNumberId) {
      userPhoneId = whatsappIntegration.details[0].phoneNumberId;
    }
  }
  
  return userPhoneId;
};

// ✅ EXISTING THUNK - MINIMAL MODIFICATION
export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async (conversationId: string, { getState, rejectWithValue }) => {
    try {
      logger.log('🔄 fetchMessages: Initial load for conversation:', conversationId);
      
      const state = getState() as RootState;
      const selectedConversation = state.whatsapp.conversations.selectedConversation;
      
      const response = await getConversationsFromId(conversationId, {
        page: 1,
        perPage: 20
      });
      
      // 🐛 DEBUG: Log the actual API response structure
      logger.log('🐛 FULL API RESPONSE:', {
        statusCode: response.data.statusCode,
        status: response.data.status,
        dataLength: response.data.data?.length,
        page: response.data.page,
        total: response.data.total
      });
      
      // ✅ FIXED: Correct field access for Axios response
      // Messages array is in response.data.data (Axios wraps the API response)
      const messages = response.data.data.map((item: any) => ({
        ...item,
        phone: selectedConversation?.mobile
      }));
      
      // ✅ FIXED: Total is at response.data.total (Axios wrapped)
      const totalMessages = response.data.total;
      const hasMore = totalMessages > 20;
      
      logger.log('📊 Pagination calc (INITIAL):', {
        totalMessages,
        loadedMessages: messages.length,
        hasMore,
        currentPage: 1
      });
      
      return {
        messages,
        isInitialLoad: true,
        totalCount: totalMessages, // ✅ FIXED: response.total
        currentPage: 1,
        hasMore: hasMore           // ✅ FIXED: response.total > 20
      };
    } catch (error: any) {
      console.error('❌ fetchMessages error:', error);
      return rejectWithValue(error.message || 'Failed to fetch messages');
    }
  }
);

// ✅ NEW: Simple thunk for loading older messages
export const fetchOlderMessages = createAsyncThunk(
  'messages/fetchOlderMessages',
  async (conversationId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentPage } = state.whatsapp.messages;
      
      // ✅ REMOVED: No more condition checks here
      // The component will handle conditions before dispatching
      
      const nextPage = currentPage + 1;
      logger.log('🔄 fetchOlderMessages: Loading page:', nextPage);
      
      const selectedConversation = state.whatsapp.conversations.selectedConversation;
      
      const response = await getConversationsFromId(conversationId, {
        page: nextPage,
        perPage: 20
      });
      
      logger.log('🐛 OLDER MESSAGES API RESPONSE:', {
        page: response.data.page,
        total: response.data.total,
        messagesReceived: response.data.data?.length
      });
      
      // ✅ FIXED: Correct field access
      const messages = response.data.data.map((item: any) => ({
        ...item,
        phone: selectedConversation?.mobile
      }));
      
      // ✅ FIXED: Use response.data.total (Axios wrapped)
      const totalMessages = response.data.total;
      const loadedSoFar = (nextPage * 20);
      const hasMore = loadedSoFar < totalMessages;
      
      logger.log('📊 Pagination calc (OLDER):', {
        totalMessages,
        loadedSoFar,
        hasMore,
        nextPage,
        newMessagesCount: messages.length
      });
      
      return {
        messages,
        isInitialLoad: false,
        hasMore: hasMore,
        currentPage: nextPage,
        totalCount: totalMessages
      };
    } catch (error: any) {
      console.error('❌ fetchOlderMessages error:', error);
      return rejectWithValue(error.message || 'Failed to fetch older messages');
    }
  }
);

// ✅ ALL OTHER EXISTING THUNKS REMAIN THE SAME
export const downloadMedia = createAsyncThunk(
  'messages/downloadMedia',
  async (message: Message, { rejectWithValue }) => {
    try {
      if (!message?.message?.id) {
        return rejectWithValue('No media ID available');
      }
      
      let mediaId = message.message.id;
      let formdata = new FormData();
      formdata.append("mediaId", mediaId);
      
      const response = await downloadChatMedia(formdata);
      
      if (response.data) {
        const blob = new Blob([response.data], {
          type: message.messageType === "document"
            ? "application/pdf"
            : message.message.mime_type
        });
        
        const url = URL.createObjectURL(blob);
        
        return {
          messageId: message._id,
          url,
          type: message.messageType,
          mimeType: message.message.mime_type
        };
      }
      
      return rejectWithValue('Failed to download media');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Media download failed');
    }
  }
);

export const processMessageReactions = createAsyncThunk(
  'messages/processMessageReactions',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const messages = state.whatsapp.messages.messages;
    
    const reactionsMap: Record<string, ReactionItem[]> = {};
    
    messages.forEach(message => {
      if (message.messageType === 'reaction') {
        const targetMessageId = 
          message.message.reaction?.message_id || 
          message.message.id;
          
        if (!targetMessageId) return;
        
        if (!reactionsMap[targetMessageId]) {
          reactionsMap[targetMessageId] = [];
        }
        
        const emoji = message.message.emoji || message.message.reaction?.emoji || '👍';
        
        const reactionObj: ReactionItem = {
          emoji: emoji,
          sender: message.sender,
          timestamp: message.createdAt
        };
        
        const existingIndex = reactionsMap[targetMessageId].findIndex(
          r => r.sender === message.sender
        );
        
        if (existingIndex >= 0) {
          const existingTimestamp = new Date(reactionsMap[targetMessageId][existingIndex].timestamp).getTime();
          const newTimestamp = new Date(message.createdAt).getTime();
          
          if (newTimestamp > existingTimestamp) {
            reactionsMap[targetMessageId][existingIndex] = reactionObj;
          }
        } else {
          reactionsMap[targetMessageId].push(reactionObj);
        }
      }
    });
    
    return reactionsMap;
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async ({ 
    phone, 
    text, 
    replyToMessageId,
    phoneId = '',
    optimisticMessageId
  }: { 
    phone: string; 
    text: string; 
    replyToMessageId?: string;
    phoneId?: string;
    optimisticMessageId?: string;
  }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const userState = state.rootReducers.userData.userDetails;
      
      const userPhoneId = getWhatsAppPhoneId(userState, phoneId);
      
      // If optimisticMessageId is provided, update its status to 'sending'
      if (optimisticMessageId) {
        dispatch(updateMessageStatus({ 
          messageId: optimisticMessageId, 
          status: 'sending' 
        }));
      }
      
      const payload: any = {
        phone,
        type: 'text',
        phoneId: userPhoneId,
        text: { body: text },
      };
      
      if (replyToMessageId) {
        payload.context = {
          message_id: replyToMessageId
        };
      }
      
      logger.log('🚀 Sending message to API', { phone, text: text.substring(0, 50) + '...' });
      
      const response = await sendMessageToLead(payload);
      
      logger.log('✅ Message sent successfully', { 
        response: response?.data || 'No response data',
        optimisticId: optimisticMessageId 
      });
      
      // Update status to 'sent' on success
      if (optimisticMessageId) {
        dispatch(updateMessageStatus({ 
          messageId: optimisticMessageId, 
          status: 'sent' 
        }));
      }
      
      const userId = typeof userState === 'undefined' ? 'current-user' : 
      Array.isArray(userState) ? 
        (userState.length > 0 && userState[0] && typeof userState[0] === 'object' ? 
          (userState[0] as {_id?: string})._id || 'current-user' : 'current-user') :
        (userState && typeof userState === 'object' ? 
          (userState as {_id?: string})._id || 'current-user' : 'current-user');
      
      return {
        optimisticMessageId,
        actualMessageId: response?.data?.messageId || `temp-${Date.now()}`,
        userId,
        success: true
      };
    } catch (error: any) {
      logger.log('❌ Message send failed', { 
        error: error.message, 
        optimisticId: optimisticMessageId 
      });
      
      // Update status to 'failed' on error
      if (optimisticMessageId) {
        dispatch(updateMessageStatus({ 
          messageId: optimisticMessageId, 
          status: 'failed' 
        }));
      }
      
      return rejectWithValue({
        error: error.message || 'Failed to send message',
        optimisticMessageId
      });
    }
  }
);

// 🔥 NEW: Retry failed message
export const retryMessage = createAsyncThunk(
  'messages/retryMessage',
  async (messageId: string, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      // ✅ FIX: Correct state path is state.whatsapp.messages.messages
      const message = state.whatsapp.messages.messages.find((m: Message) => m._id === messageId);

      if (!message) {
        logger.log('❌ Message not found in state:', {
          messageId,
          totalMessages: state.whatsapp.messages.messages.length
        });
        throw new Error('Message not found');
      }

      logger.log('🔄 Retrying failed message:', { messageId, message });

      // Get the selected conversation to extract phone number
      const selectedConversation = state.whatsapp.conversations.selectedConversation;
      const phone = selectedConversation?.mobile || message.phone;

      if (!phone) {
        throw new Error('Cannot retry: no phone number available');
      }

      // Extract the original message text
      let text = '';
      if (typeof message.message.body === 'string') {
        text = message.message.body;
      } else if (typeof message.message.body === 'object' && message.message.body?.text?.body) {
        text = message.message.body.text.body;
      }

      if (!text) {
        throw new Error('Cannot retry: message has no text content');
      }

      logger.log('🔄 Retry details:', { phone, text: text.substring(0, 50) });

      // Update status to 'sending'
      dispatch(updateMessageStatus({ messageId, status: 'sending' }));

      // Resend using the existing sendMessage action
      const result = await dispatch(sendMessage({
        phone,
        text,
        phoneId: '', // phoneId is extracted from userState in sendMessage
        optimisticMessageId: messageId
      })).unwrap();

      logger.log('✅ Message retry successful:', result);

      return { messageId, success: true };
    } catch (error: any) {
      logger.log('❌ Message retry failed:', { error: error.message, messageId });

      // Update status back to 'failed'
      dispatch(updateMessageStatus({ messageId, status: 'failed' }));

      return rejectWithValue({
        error: error.message || 'Failed to retry message',
        messageId
      });
    }
  }
);

// 🔥 NEW: Send WhatsApp template
export const sendTemplate = createAsyncThunk(
  'messages/sendTemplate',
  async ({
    phone,
    templateId,
    phoneId = '',
    optimisticMessageId
  }: {
    phone: string;
    templateId: string;
    phoneId?: string;
    optimisticMessageId?: string;
  }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const userState = state.rootReducers.userData.userDetails;

      const userPhoneId = getWhatsAppPhoneId(userState, phoneId);

      // If optimisticMessageId is provided, update its status to 'sending'
      if (optimisticMessageId) {
        dispatch(updateMessageStatus({
          messageId: optimisticMessageId,
          status: 'sending'
        }));
      }

      const payload: any = {
        phone,
        type: 'template',
        phoneId: userPhoneId,
        template: templateId,
      };

      logger.log('🚀 Sending template to API', { phone, templateId });

      const response = await sendMessageToLead(payload);

      logger.log('✅ Template sent successfully', {
        response: response?.data || 'No response data',
        optimisticId: optimisticMessageId
      });

      // Update status to 'sent' on success
      if (optimisticMessageId) {
        dispatch(updateMessageStatus({
          messageId: optimisticMessageId,
          status: 'sent'
        }));
      }

      const userId = typeof userState === 'undefined' ? 'current-user' :
      Array.isArray(userState) ?
        (userState.length > 0 && userState[0] && typeof userState[0] === 'object' ?
          (userState[0] as {_id?: string})._id || 'current-user' : 'current-user') :
        (userState && typeof userState === 'object' ?
          (userState as {_id?: string})._id || 'current-user' : 'current-user');

      return {
        optimisticMessageId,
        actualMessageId: response?.data?.messageId || `temp-${Date.now()}`,
        userId,
        success: true
      };
    } catch (error: any) {
      logger.log('❌ Template send failed', {
        error: error.message,
        optimisticId: optimisticMessageId
      });

      // Update status to 'failed' on error
      if (optimisticMessageId) {
        dispatch(updateMessageStatus({
          messageId: optimisticMessageId,
          status: 'failed'
        }));
      }

      return rejectWithValue({
        error: error.message || 'Failed to send template',
        optimisticMessageId
      });
    }
  }
);

export const sendReaction = createAsyncThunk(
  'messages/sendReaction',
  async ({
    messageId,
    emoji,
    phone,
    phoneId = ''
  }: { 
    messageId: string; 
    emoji: string; 
    phone: string;
    phoneId?: string;
  }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const userState = state.rootReducers.userData.userDetails;
      
      const userPhoneId = getWhatsAppPhoneId(userState, phoneId);
      
      const payload = {
        type: "reaction",
        reaction: {
          message_id: messageId,
          emoji
        },
        phone,
        phoneId: userPhoneId
      };
      
      await sendMessageToLead(payload);
      
      const userId = typeof userState === 'undefined' ? 'current-user' : 
      Array.isArray(userState) ? 
        (userState.length > 0 && userState[0] && typeof userState[0] === 'object' ? 
          (userState[0] as {_id?: string})._id || 'current-user' : 'current-user') :
        (userState && typeof userState === 'object' ? 
          (userState as {_id?: string})._id || 'current-user' : 'current-user');
      
      return {
        messageId,
        emoji,
        userId
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send reaction');
    }
  }
);

// ✅ SLICE WITH MINIMAL ADDITIONS
const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setCurrentConversationId: (state, action: PayloadAction<string | null>) => {
  logger.log('🔥 setCurrentConversationId CALLED:', action.payload);
  logger.log('🔥 setCurrentConversationId: Current state.currentConversationId =', state.currentConversationId);
  logger.log('🔥 setCurrentConversationId: Current state.hasMoreMessages =', state.hasMoreMessages);
  
  state.currentConversationId = action.payload;
  
  // Clear messages when changing conversation
  if (action.payload === null || action.payload !== state.currentConversationId) {
    logger.log('🔥 setCurrentConversationId: RESETTING STATE - condition met');
    
    state.messages = [];
    state.messagesByDate = {};
    state.replyContext = null;
    state.lead = null;
    state.mediaDownloading = {};
    state.mediaErrors = {};
    
    // ✅ NEW: Reset pagination state
    state.hasMoreMessages = true;
    state.loadingOlderMessages = false;
    state.currentPage = 1;
    
    logger.log('🔥 setCurrentConversationId: hasMoreMessages RESET TO =', state.hasMoreMessages);
  } else {
    logger.log('🔥 setCurrentConversationId: NOT resetting - condition not met');
  }
  
  logger.log('🔥 setCurrentConversationId: Final state.hasMoreMessages =', state.hasMoreMessages);
},
    
    setReplyContext: (state, action: PayloadAction<Message | null>) => {
      state.replyContext = action.payload;
    },
    
    clearReplyContext: (state) => {
      state.replyContext = null;
    },
    
    addMessage: (state, action: PayloadAction<Message>) => {
      const message = action.payload;
      
      const exists = state.messages.some(m => m._id === message._id);
      if (!exists) {
        state.messages.push(message);
        
        const date = moment(message.createdAt).format('L');
        const time = moment(message.createdAt).format('hh:mm a');
        
        if (!state.messagesByDate[date]) {
          state.messagesByDate[date] = {};
        }
        
        if (!state.messagesByDate[date][time]) {
          state.messagesByDate[date][time] = [];
        }
        
        state.messagesByDate[date][time].push(message);
      }
    },
    
    updateMessageStatus: (state, action: PayloadAction<{ 
      messageId: string;
      status: 'sent' | 'delivered' | 'read' | 'failed' | 'sending';
    }>) => {
      const { messageId, status } = action.payload;
      
      state.messages = state.messages.map(message => 
        message._id === messageId 
          ? { ...message, messageDelivery: status, status: status } 
          : message
      );
      
      Object.keys(state.messagesByDate).forEach(date => {
        Object.keys(state.messagesByDate[date]).forEach(time => {
          state.messagesByDate[date][time] = state.messagesByDate[date][time].map(message => 
            message._id === messageId 
              ? { ...message, messageDelivery: status, status: status } 
              : message
          );
        });
      });
    },

    // 🔥 NEW: Add optimistic message immediately
    addOptimisticMessage: (state, action: PayloadAction<{
      text: string;
      phone: string;
      replyToMessageId?: string;
      userId: string;
    }>) => {
      const { text, phone, replyToMessageId, userId } = action.payload;
      
      const optimisticMessage: Message = {
        _id: `temp-${Date.now()}-${Math.random()}`,
        sender: userId,
        message: {
          body: text,
        },
        createdAt: new Date().toISOString(),
        messageDelivery: 'sending',
        status: 'sending',
        messageType: 'text',
        phone: phone,
      };
      
      // Add to messages array
      state.messages.push(optimisticMessage);
      
      // Add to messagesByDate grouping
      const date = moment(optimisticMessage.createdAt).format('L');
      const time = moment(optimisticMessage.createdAt).format('hh:mm a');
      
      if (!state.messagesByDate[date]) {
        state.messagesByDate[date] = {};
      }
      
      if (!state.messagesByDate[date][time]) {
        state.messagesByDate[date][time] = [];
      }
      
      state.messagesByDate[date][time].push(optimisticMessage);
      
      logger.log('🚀 Optimistic message added to state', {
        id: optimisticMessage._id,
        text: text.substring(0, 50) + '...',
        status: 'sending'
      });
    },
    
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    
    clearMessages: (state) => {
  logger.log('🔥 clearMessages CALLED');
  logger.log('🔥 clearMessages: Before - hasMoreMessages =', state.hasMoreMessages);
  
  state.messages = [];
  state.messagesByDate = {};
  state.currentConversationId = null;
  state.replyContext = null;
  state.lead = null;
  state.mediaDownloading = {};
  state.mediaErrors = {};
  
  // ✅ NEW: Reset pagination
  state.hasMoreMessages = true;
  state.loadingOlderMessages = false;
  state.currentPage = 1;
  
  logger.log('🔥 clearMessages: After - hasMoreMessages =', state.hasMoreMessages);
}
  },
  extraReducers: (builder) => {
    builder
      // ✅ UPDATED: Handle fetchMessages with pagination
      .addCase(fetchMessages.pending, (state) => {
        state.status = 'loading';
      })
     .addCase(fetchMessages.fulfilled, (state, action) => {
  
  
  state.status = 'succeeded';
  
  const { messages, isInitialLoad, totalCount, currentPage } = action.payload;
  let messagesArray: Message[] = [];
  let lead: Lead | null = null;
  
  messages.forEach((item: any) => {
    if (item.lead) {
      lead = item.lead;
    }
    
    // Check if we already have this message with content (optimistic message)
    const existingMessage = state.messages.find(msg => msg._id === item._id || msg.messageId === item.messageId);
    
    // If server returns empty message content but we have existing content, preserve it
    let messageContent = item.message;
    if (existingMessage && 
        existingMessage.message && 
        (!item.message || Object.keys(item.message).length === 0 || !item.message.body) &&
        (existingMessage.message.body)) {
      logger.log('🔄 Preserving existing message content for:', item._id);
      messageContent = existingMessage.message;
    }
    
    messagesArray.unshift({
      _id: item._id,
      messageId: item.messageId,
      sender: item.sender,
      messageType: item.messageType,
      message: messageContent,
      context: item.context,  // 🔥 CRITICAL: Include context for reply functionality
      createdAt: item.createdAt,
      messageDelivery: item.messageDelivery,
      status: item.status,
      statusLogs: item.statusLogs,
      downloadUrl: item.downloadUrl,
      reactions: item.reactions
    });
  });
  
  // ✅ Set pagination state
  state.messages = messagesArray;
  state.messagesByDate = groupMessagesByDateTime(messagesArray);
  state.lead = lead;
  state.currentPage = currentPage;
  
  // 🔥 DEBUG: Before setting hasMoreMessages
  
  
  state.hasMoreMessages = action.payload.hasMore || false;
  
  // 🔥 DEBUG: After setting hasMoreMessages
  logger.log('🔥 REDUCER: New state.hasMoreMessages =', state.hasMoreMessages);
  logger.log('🔥 REDUCER: fetchMessages.fulfilled COMPLETED');
})
      .addCase(fetchMessages.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      
      // ✅ NEW: Handle fetchOlderMessages
      .addCase(fetchOlderMessages.pending, (state) => {
        state.loadingOlderMessages = true;
      })
      .addCase(fetchOlderMessages.fulfilled, (state, action) => {
        state.loadingOlderMessages = false;
        
        const { messages, hasMore, currentPage } = action.payload;
        
        if (messages.length > 0) {
          let olderMessages: Message[] = [];
          
          messages.forEach((item: any) => {
            // Check if we already have this message with content (optimistic message)
            const existingMessage = state.messages.find(msg => msg._id === item._id || msg.messageId === item.messageId);
            
            // If server returns empty message content but we have existing content, preserve it
            let messageContent = item.message;
            if (existingMessage && 
                existingMessage.message && 
                (!item.message || Object.keys(item.message).length === 0 || !item.message.body) &&
                (existingMessage.message.body)) {
              logger.log('🔄 Preserving existing message content for older message:', item._id);
              messageContent = existingMessage.message;
            }
            
            olderMessages.unshift({
              _id: item._id,
              messageId: item.messageId,
              sender: item.sender,
              messageType: item.messageType,
              message: messageContent,
              context: item.context,  // 🔥 CRITICAL: Include context for reply functionality
              createdAt: item.createdAt,
              messageDelivery: item.messageDelivery,
              status: item.status,
              statusLogs: item.statusLogs,
              downloadUrl: item.downloadUrl,
              reactions: item.reactions
            });
          });
          
          // ✅ PREPEND older messages to the beginning
          state.messages = [...olderMessages, ...state.messages];
          state.messagesByDate = groupMessagesByDateTime(state.messages);
          state.currentPage = currentPage || state.currentPage;
        }
        
        state.hasMoreMessages = hasMore;
      })
      .addCase(fetchOlderMessages.rejected, (state, action) => {
        state.loadingOlderMessages = false;
        state.error = action.payload as string;
      })
      
      // ✅ ALL OTHER EXISTING CASES REMAIN THE SAME
      .addCase(processMessageReactions.fulfilled, (state, action) => {
        state.messageReactions = action.payload;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { optimisticMessageId, actualMessageId, success } = action.payload;
        
        logger.log('🎉 sendMessage.fulfilled', { 
          optimisticMessageId, 
          actualMessageId, 
          success 
        });
        
        // If we have an optimistic message, update its ID to the actual one from the server
        if (optimisticMessageId && actualMessageId && success) {
          state.messages = state.messages.map(message => 
            message._id === optimisticMessageId 
              ? { ...message, _id: actualMessageId, messageDelivery: 'sent', status: 'sent' }
              : message
          );
          
          // Also update in messagesByDate
          Object.keys(state.messagesByDate).forEach(date => {
            Object.keys(state.messagesByDate[date]).forEach(time => {
              state.messagesByDate[date][time] = state.messagesByDate[date][time].map(message => 
                message._id === optimisticMessageId 
                  ? { ...message, _id: actualMessageId, messageDelivery: 'sent', status: 'sent' }
                  : message
              );
            });
          });
        }
        
        // Reply context is already cleared in the UI optimistically
      })
      .addCase(sendReaction.fulfilled, (state, action) => {
        const { messageId, emoji, userId } = action.payload;
        
        state.messages = state.messages.map(message => {
          if (message._id === messageId || message.messageId === messageId) {
            return {
              ...message,
              reactions: {
                ...(message.reactions || {}),
                [userId]: emoji
              }
            };
          }
          return message;
        });
        
        Object.keys(state.messagesByDate).forEach(date => {
          Object.keys(state.messagesByDate[date]).forEach(time => {
            state.messagesByDate[date][time] = state.messagesByDate[date][time].map(message => {
              if (message._id === messageId || message.messageId === messageId) {
                return {
                  ...message,
                  reactions: {
                    ...(message.reactions || {}),
                    [userId]: emoji
                  }
                };
              }
              return message;
            });
          });
        });
      })
      .addCase(downloadMedia.pending, (state, action) => {
        const message = action.meta.arg;
        state.mediaDownloading[message._id] = true;
        state.mediaErrors[message._id] = '';
      })
      .addCase(downloadMedia.fulfilled, (state, action) => {
        const { messageId } = action.payload;
        state.mediaDownloading[messageId] = false;
      })
      .addCase(downloadMedia.rejected, (state, action) => {
        const message = action.meta.arg;
        state.mediaDownloading[message._id] = false;
        state.mediaErrors[message._id] = action.payload as string;
      });
  }
});

// ✅ EXPORT EXISTING + NEW ACTIONS
export const { 
  setCurrentConversationId, 
  setReplyContext, 
  clearReplyContext, 
  addMessage, 
  updateMessageStatus, 
  addOptimisticMessage,
  setSearchText, 
  clearMessages 
} = messagesSlice.actions;

// ✅ EXISTING + NEW SELECTORS
export const selectMessages = (state: RootState) => state.whatsapp.messages.messages;
export const selectMessagesByDate = (state: RootState) => state.whatsapp.messages.messagesByDate;
export const selectCurrentConversationId = (state: RootState) => state.whatsapp.messages.currentConversationId;
export const selectMessageReactionsByMessageId = (messageId: string) => 
  (state: RootState): ReactionItem[] => state.whatsapp.messages.messageReactions[messageId] || [];
export const selectReplyContext = (state: RootState) => state.whatsapp.messages.replyContext;
export const selectLead = (state: RootState) => state.whatsapp.messages.lead;
export const selectMessagesStatus = (state: RootState) => state.whatsapp.messages.status;
export const selectSearchText = (state: RootState) => state.whatsapp.messages.searchText;
export const selectMediaDownloading = (messageId: string) => 
  (state: RootState) => state.whatsapp.messages.mediaDownloading[messageId];
export const selectMediaError = (messageId: string) => 
  (state: RootState) => state.whatsapp.messages.mediaErrors[messageId];

// ✅ NEW: Pagination selectors
export const selectHasMoreMessages = (state: RootState) => state.whatsapp.messages.hasMoreMessages;
export const selectLoadingOlderMessages = (state: RootState) => state.whatsapp.messages.loadingOlderMessages;
export const selectCurrentPage = (state: RootState) => state.whatsapp.messages.currentPage;

export default messagesSlice.reducer;